import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import logo from "../assets/lp-uscis/alpha-logo.png";
import ataLogo from "../assets/lp-uscis/ata-logo.png";
import trustedLogo from "../assets/lp-uscis/trusted.png";
import "./UscisCampaignLanding.css";

// Landing da campanha Google Ads "Tradução certificada USCIS" (PT, rota
// /traducao-certificada-uscis). As versões /es e /en seguem no LandingPage.
// Sem o Header global (ver App.js) — a página tem cabeçalho próprio.

const WHATSAPP = "https://api.whatsapp.com/send?phone=14073859794";
const GOOGLE_REVIEWS = "https://www.google.com/search?q=alpha+translations+orlando+reviews";

// Cupom da campanha: faixa e selo somem sozinhos depois do fim da oferta
// (fim do dia 21/10/2026, horário de Orlando).
const PROMO_END = new Date("2026-10-21T23:59:59-04:00");

const DOCS = [
  "Certidão de nascimento",
  "Certidão de casamento",
  "Diplomas e históricos",
  "Antecedentes criminais",
  "Carteira de habilitação",
  "Documentos imigratórios",
];

const UscisCampaignLanding = () => {
  const promoActive = Date.now() <= PROMO_END.getTime();

  return (
    <div className="uscis-lp">
      <Seo
        title="Tradução Certificada para USCIS"
        description="Tradução certificada de português ou espanhol para inglês, aceita pelo USCIS. Orçamento automático e instantâneo, revisão de qualidade e atendimento 100% online."
        path="/traducao-certificada-uscis"
      />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {promoActive && (
        <div className="promo">
          <strong>Use o cupom USCIS2 no checkout e economize US$2 por página</strong>
          <span>Oferta da campanha até 21 de outubro</span>
        </div>
      )}

      <header>
        <div className="wrap header-row">
          <img className="logo" src={logo} alt="Alpha Translations" />
          <div className="header-actions">
            <a className="header-link" href={WHATSAPP} target="_blank" rel="noopener noreferrer">
              Dúvidas? Fale conosco
            </a>
            <Link className="header-button" to="/login">
              Área do Cliente
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <div className="eyebrow">Tradução certificada para imigração</div>
              <h1>Seus documentos prontos para o USCIS</h1>
              <p className="hero-copy">
                Tradução de português ou espanhol para inglês, com <strong>certificação de precisão</strong>,
                revisão de qualidade e atendimento 100% online.
              </p>
              <div className="offer">
                {promoActive ? (
                  <>
                    <span className="price-highlight">
                      A partir de <strong>US$10</strong>/página
                    </span>
                    <span className="coupon-tag">
                      Use o cupom <b>USCIS2</b> no checkout
                    </span>
                  </>
                ) : (
                  // Fim da campanha: sem cupom, o preço volta a ser US$12.
                  <span className="price-highlight">
                    A partir de <strong>US$12</strong>/página
                  </span>
                )}
              </div>
              <div className="cta-row">
                <Link className="cta cta-primary" to="/register">
                  Enviar documentos e ver preço agora
                </Link>
                <a className="cta cta-secondary" href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                  Falar no WhatsApp
                </a>
              </div>
              <div className="fine-print">
                <span>Orçamento automático e instantâneo</span>
                <span>Upload seguro</span>
                <span>Sem compromisso</span>
              </div>
            </div>

            <aside className="quote-card">
              <h2>Comece em poucos minutos</h2>
              <p>Envie os arquivos: o sistema conta as páginas automaticamente e mostra seu orçamento na hora.</p>
              <div className="quote-steps">
                {[
                  ["Envie os documentos", "PDF ou foto pelo portal seguro"],
                  ["Contagem automática", "O sistema identifica e conta as páginas"],
                  ["Orçamento instantâneo", "Veja valor e prazo antes de pagar"],
                ].map(([title, sub], i) => (
                  <div className="quote-step" key={title}>
                    <span className="num">{i + 1}</span>
                    <div>
                      <strong>{title}</strong>
                      <small>{sub}</small>
                    </div>
                  </div>
                ))}
              </div>
              <Link className="cta cta-primary full" to="/register">
                Calcular meu orçamento agora
              </Link>
              <p className="secure">Ambiente seguro · Sem compromisso</p>
            </aside>
          </div>
        </section>

        <section className="trustbar" aria-label="Credenciais e avaliações">
          <div className="wrap trust-grid">
            <a
              className="trust-link"
              href="https://www.atanet.org/member-directory/alpha-translations-llc/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={ataLogo} alt="American Translators Association" />
              <span className="trust-copy">
                <strong>ATA Corporate Member</strong>
                <span>M-103039</span>
              </span>
            </a>
            <a
              className="trust-link"
              href="https://www.bbb.org/us/fl/orlando/profile/translators/alpha-translations-0733-235975216/#sealclick"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="bbb-mark">BBB</span>
              <span className="trust-copy">
                <strong>BBB Accredited Business</strong>
                <span>Classificação A+</span>
              </span>
            </a>
            <a className="trust-link" href={GOOGLE_REVIEWS} target="_blank" rel="noopener noreferrer">
              <span className="google-stars">★★★★★</span>
              <span className="trust-copy">
                <strong>5.0 no Google</strong>
                <span>Avaliações públicas</span>
              </span>
            </a>
            <div className="trust-link">
              <img src={trustedLogo} alt="Trusted Company" />
            </div>
          </div>
        </section>

        <section className="compact-section">
          <div className="wrap">
            <div className="section-title">
              <h2>O que você recebe</h2>
              <p>Um serviço objetivo, preparado para reduzir dúvidas e facilitar a apresentação dos documentos.</p>
            </div>
            <div className="benefits">
              <article className="benefit">
                <h3>Certificação de precisão</h3>
                <p>Declaração assinada confirmando que a tradução é completa e fiel ao documento apresentado.</p>
              </article>
              <article className="benefit">
                <h3>Revisão de qualidade</h3>
                <p>Conferência linguística, terminológica, visual e documental antes da entrega.</p>
              </article>
              <article className="benefit">
                <h3>Suporte em português</h3>
                <p>Atendimento humano durante o envio, orçamento e acompanhamento do projeto.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="compact-section documents">
          <div className="wrap">
            <div className="section-title">
              <h2>Documentos mais solicitados</h2>
              <p>Consulte a equipe para outros tipos de documento ou idiomas.</p>
            </div>
            <div className="doc-grid">
              {DOCS.map((d) => (
                <div className="doc" key={d}>{d}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="review-band">
          <div className="wrap review-box">
            <div className="review-score">5.0</div>
            <div className="review-text">
              <strong>
                <span className="google-stars">★★★★★</span> Avaliações no Google
              </strong>
              <p>Confira diretamente no Google as experiências publicadas por clientes da Alpha Translations.</p>
            </div>
            <a className="review-link" href={GOOGLE_REVIEWS} target="_blank" rel="noopener noreferrer">
              Ver avaliações reais
            </a>
          </div>
        </section>

        <section className="final">
          <div className="wrap final-row">
            <div>
              <h2>Descubra agora quanto ficará sua tradução</h2>
              <p>Envie os documentos: o sistema conta as páginas e apresenta o orçamento automaticamente, sem compromisso.</p>
            </div>
            <Link className="cta" to="/register">
              Calcular meu orçamento
            </Link>
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap footer-row">
          <span>© {new Date().getFullYear()} Alpha Translations LLC</span>
          <span>support@alpha-translations.com · +1 (407) 385-9794</span>
        </div>
      </footer>
    </div>
  );
};

export default UscisCampaignLanding;
